import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import CommonTable from '../../../../components/common-table/CommonTable';
import Tr from '../../../../components/common-table/components/Tr';
import UserTableItem from './UserTableItem';
import Th from '../../../../components/common-table/components/Th';
import ThWithSort from '../../../../components/common-table/components/ThWithSort';
import CommonPagination from '../../../../components/common-pagination/CommonPagination';
import { changeTablePage, changeTableSort } from '../../../../redux/user-management/userManagementReducer';
import calculateSortStr from '../../../../utils/calculateSortValue';
import { deleteUser, disableUser, getUsers } from '../../../../redux/user-management/userManagementActions';
import ConfirmationModal from '../../../../components/confirmation-modal/ConfirmationModal';
import DeleteUserLabel from './DeleteUserLabel';
import useUserRolesDropdown from '../../../../hooks/useUserRolesDropdown';
import EnableUserLabel from './EnableUserLabel';
import ChangePasswordModal from '../../../change-password-modal/ChangePasswordModal';

import scss from './UserTable.module.scss';

const UserTable = () => {
  const { t } = useTranslation('userManagement');
  const dispatch = useDispatch();
  const userRoles = useUserRolesDropdown();

  const content = useSelector((state) => state.userManagement.usersList);
  const isLoading = useSelector((state) => state.userManagement.isLoading);
  const { page, size, sort, totalElements } = useSelector((state) => state.userManagement.tableSettings);
  const [selectedUser, setSelectedUser] = useState({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);

  const handleChangePage = useCallback(
    (newPage) => {
      dispatch(changeTablePage(newPage));
    },
    [dispatch],
  );

  const handleChangeSort = useCallback(
    (sortValue) => {
      dispatch(changeTableSort(calculateSortStr({ currentSort: sort, newSortKey: sortValue })));
    },
    [dispatch, sort],
  );

  const handleCloseModals = useCallback(() => {
    setIsBlockModalOpen(false);
    setIsDeleteModalOpen(false);
    setIsChangePasswordModalOpen(false);
  }, []);

  const handleOpenDeleteModal = useCallback((user) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  }, []);

  const handleOpenEnableModal = useCallback((user) => {
    setSelectedUser(user);
    setIsBlockModalOpen(true);
  }, []);

  const handleOpenChangePasswordModal = useCallback((user) => {
    setSelectedUser(user);
    setIsChangePasswordModalOpen(true);
  }, []);

  const handleDisableUser = useCallback(() => {
    dispatch(disableUser({ id: selectedUser.id, enabled: selectedUser.enabled }));
  }, [dispatch, selectedUser]);

  const handleDeleteUser = useCallback(() => {
    dispatch(deleteUser({ id: selectedUser.id }));
  }, [dispatch, selectedUser]);

  useEffect(() => {
    if (typeof page !== 'undefined' && typeof size !== 'undefined' && typeof sort !== 'undefined') {
      dispatch(getUsers());
    }
  }, [dispatch, page, size, sort]);

  return (
    <div className={scss.tableWrapper}>
      <CommonTable
        content={content}
        isLoading={isLoading}
        renderBody={(item, index) => (
          <UserTableItem
            key={item.id}
            user={item}
            rowRank={page * size + (index + 1)}
            userRoles={userRoles}
            onOpenDeleteModal={handleOpenDeleteModal}
            onOpenEnableModal={handleOpenEnableModal}
            onChangePasswordModal={handleOpenChangePasswordModal}
          />
        )}>
        <Tr>
          <Th extraClasses={scss.cellId}>#</Th>
          <ThWithSort
            label={t('name')}
            columnKey='name'
            sortStr={sort}
            onSort={handleChangeSort}
            extraThClass={scss.cellLeft}
          />
          <ThWithSort
            label={t('email')}
            columnKey='username'
            sortStr={sort}
            onSort={handleChangeSort}
            extraThClass={scss.cellLeft}
          />
          <ThWithSort
            label={t('status')}
            columnKey='enabled'
            sortStr={sort}
            onSort={handleChangeSort}
            extraThClass={scss.cellLeft}
          />
          <ThWithSort
            label={t('added')}
            columnKey='createdOn'
            sortStr={sort}
            onSort={handleChangeSort}
            extraThClass={scss.cellLeft}
          />
          <ThWithSort
            label={t('type')}
            columnKey='roleName'
            sortStr={sort}
            onSort={handleChangeSort}
            extraThClass={scss.cellLeft}
          />
          <Th extraClasses='cell-btn'></Th>
        </Tr>
      </CommonTable>

      <div className={scss.paginationWrapper}>
        <CommonPagination
          activePage={page}
          itemsPerPage={size}
          totalItemsCount={totalElements}
          onChangePage={handleChangePage}
        />
      </div>

      <ConfirmationModal
        isOpen={isBlockModalOpen}
        onClose={handleCloseModals}
        onCancel={handleCloseModals}
        onConfirm={handleDisableUser}
        subLabel={<EnableUserLabel name={selectedUser.name} enabled={selectedUser.enabled} />}
      />
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseModals}
        onCancel={handleCloseModals}
        onConfirm={handleDeleteUser}
        subLabel={<DeleteUserLabel name={selectedUser.name} />}
      />
      <ChangePasswordModal isOpen={isChangePasswordModalOpen} onClose={handleCloseModals} id={selectedUser.id} />
    </div>
  );
};

export default UserTable;
